import clsx from "clsx";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import EnrollmentsClientService from "../../services/EnrollmentsClientService";
import { EnrollmentStatus, JourneyAnchors } from "../../constants";
import { Box, Grid2, Typography, Divider } from "@mui/material";
import { ClientProgramProgressCard } from "../card/ClientProgramProgressCard";
import React from "react";
import { CardPagination } from "../pagination/CardPagination";
import makeStyles from "@mui/styles/makeStyles";
import { ActiveProgramSkeletonCard } from "../loading/ActiveProgramSkeletonCard";

export const CLIENT_PROFILE_PROGRAMS_LIST_QUERY_KEY =
  "client-overview-programs-list";
const CLIENT_PROGRAMS_PAGE_SIZE = 3;

interface IClientDetailsProgramProps {
  clientId: number;
}
const useStyles = makeStyles((theme) => ({
  pagination: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  divider: {
    //use negative margin to offset padding in the parent container
    margin: theme.spacing(2, -3, 4),
    [theme.breakpoints.up("md")]: {
      margin: theme.spacing(2, 0),
    },
  },
}));

const ClientDetailsPrograms = ({ clientId }: IClientDetailsProgramProps) => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const s = useStyles();
  const {
    data,
    isFetching,
    isPlaceholderData: isPrevPageData,
    isLoading: isFirstPageFirstFetch,
  } = useQuery({
    placeholderData: keepPreviousData,
    queryKey: [
      CLIENT_PROFILE_PROGRAMS_LIST_QUERY_KEY,
      { clientId, currentPage },
    ],
    queryFn: () =>
      EnrollmentsClientService.getClientEnrolments({
        status: EnrollmentStatus.ALL,
        pageNumber: currentPage,
        pageSize: CLIENT_PROGRAMS_PAGE_SIZE,
        clientId: clientId,
      }),
  });

  const showCards = data?.items?.length && !isPrevPageData;
  const showSkeleton = isFirstPageFirstFetch || (isFetching && isPrevPageData);
  const totalItems = data?.items?.length ?? 0;

  return showSkeleton || showCards ? (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        padding: 3,
      }}
    >
      {showCards
        ? data?.items?.map((enrollment, index) => (
            <React.Fragment key={enrollment.id}>
              <Grid2
                size={12}
                className={clsx({
                  [JourneyAnchors.CLIENT_FIRST_PROGRAM]: index === 0,
                })}
              >
                <ClientProgramProgressCard enrollment={enrollment} />
              </Grid2>

              {index < totalItems - 1 && <Divider className={s.divider} />}
            </React.Fragment>
          ))
        : showSkeleton
          ? Array.from({ length: CLIENT_PROGRAMS_PAGE_SIZE }).map(
              (_, index) => (
                <React.Fragment key={index}>
                  <Grid2 size={12}>
                    <ActiveProgramSkeletonCard />
                  </Grid2>
                  {index < CLIENT_PROGRAMS_PAGE_SIZE - 1 && (
                    <Divider className={s.divider} />
                  )}
                </React.Fragment>
              ),
            )
          : null}
      {data?.totalPages > 1 && (
        <Box className={s.pagination}>
          <CardPagination
            page={currentPage}
            count={data?.totalPages}
            onChange={(_, page) => setCurrentPage(page)}
          />
        </Box>
      )}
    </Box>
  ) : null;
};

export default ClientDetailsPrograms;
